'use client';

import { FC } from 'react';
import Link from 'next/link';
import {
  CHALLENGE_LABEL,
  challengeRulesNote,
  subscriptionPath,
  type ChallengeListing,
  type DayPlayStatus,
} from '@puzzlio/core';
import { Button, LockBadgeIcon, Title } from '@puzzlio/ui';
import { cn } from '@/app/lib/utils';
import { GAME_CONFIG } from '@/app/games/config';
import { PuzzleDifficulty } from '@/app/components/new-ui/molecules/puzzle-difficulty';
import { trackSubscriptionPromptClicked } from '@/app/lib/analytics-events';

const STATUS_LABEL: Record<DayPlayStatus, string> = {
  complete: 'Solved',
  incomplete: 'Keep Going',
  'not-started': 'Play',
};

/**
 * One weekly challenge: its game, difficulty and size, and the player's progress on it. A `locked` card,
 * a non-subscriber's, goes straight to subscribing, which is the only thing it can lead to.
 */
export const ChallengeCard: FC<{
  challenge: ChallengeListing;
  status: DayPlayStatus;
  locked: boolean;
  className?: string;
}> = ({ challenge, status, locked, className }) => {
  const { gameType, week, difficulty, width, height } = challenge;
  const { label, cardHeaderClassName, buttonColor, challengeURL } = GAME_CONFIG[gameType];
  const details = [challengeRulesNote(challenge), width != null && height != null && `${width}×${height}`].filter(
    Boolean
  );

  return (
    <Link
      prefetch={false}
      href={locked ? subscriptionPath('challenge', gameType) : challengeURL(week)}
      onClick={locked ? () => trackSubscriptionPromptClicked(gameType, 'challenge') : undefined}
      data-testid={`challenge-card-${gameType}-${week}`}
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border-[0.25rem] border-black bg-white shadow-[0.2rem_0.2rem_0_0_var(--gray-300),0.2rem_0.2rem_0_0.2rem_var(--black)]',
        className
      )}
    >
      <div className={cn('border-b px-5 py-3', cardHeaderClassName)}>
        <Title className="text-2xl">{label}</Title>
        <span className="font-quicksand text-sm font-bold text-black opacity-70">{CHALLENGE_LABEL}</span>
      </div>
      <div className="flex flex-col gap-3 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <PuzzleDifficulty difficulty={difficulty} maxDifficulty={5} iconClassName="size-5 md:size-5" />
          {details.length > 0 && (
            <span className="font-quicksand text-sm font-bold text-black opacity-60">{details.join(' · ')}</span>
          )}
        </div>
        {/* A locked button carries the padlock badge every subscriber-gated control does (see
            HighlightToggleButton). */}
        <div className="relative">
          <Button
            variant="gameplay"
            size="page"
            color={locked ? undefined : buttonColor}
            className="pointer-events-none w-full"
            asChild
          >
            <span>{locked ? 'Subscribe to play' : STATUS_LABEL[status]}</span>
          </Button>
          {locked && (
            <LockBadgeIcon className="pointer-events-none absolute -top-2 -right-2 z-10 size-6 md:-top-3 md:-right-3 md:size-8" />
          )}
        </div>
      </div>
    </Link>
  );
};
